import { type AxiosError, type AxiosResponse, isAxiosError } from "axios";
import { z } from "zod";

const cbhApiErrorSchema = z.object({
  code: z.string(),
  detail: z.string(),
  id: z.string(),
  status: z.string(),
  title: z.string().optional(),
  reasons: z.array(z.string()).optional(),
});

const cbhApiErrorsSchema = z.object({
  errors: z.array(cbhApiErrorSchema),
});

export type CbhApiError = z.infer<typeof cbhApiErrorSchema>;
export type CbhApiErrors = z.infer<typeof cbhApiErrorsSchema>;

export function isCbhApiAxiosError(error: unknown): error is AxiosError<CbhApiErrors> & {
  // this is safe since we assert that response is defined
  response: AxiosResponse<CbhApiErrors>;
} {
  if (!isAxiosError(error) || !error.response) {
    return false;
  }

  return cbhApiErrorsSchema.safeParse(error.response.data).success;
}

export function isCbhApiSuperagentError(error: unknown): error is Error & {
  status: number;
  response: { body: CbhApiErrors };
} {
  if (!(error instanceof Error) || !("response" in error) || !("status" in error)) {
    return false;
  }

  const responseError = error as Error & { response?: { body?: unknown } };
  let body: unknown = responseError.response?.body;

  if (typeof body === "string") {
    try {
      body = JSON.parse(body) as unknown;
    } catch {
      return false;
    }
  }

  return cbhApiErrorsSchema.safeParse(body).success;
}

export function getCbhApiErrorByCode(error: unknown, code: string): CbhApiError | undefined {
  if (!isCbhApiSuperagentError(error)) {
    return undefined;
  }

  return error.response.body.errors.find((apiError) => apiError.code === code);
}

export function formatCbhApiError(error: CbhApiError): string {
  const detail = error.detail ?? error.title;
  const reasonsText =
    Array.isArray(error.reasons) && error.reasons.length > 0
      ? `Reasons: ${error.reasons.join(", ")}`
      : "";

  if (detail && reasonsText) {
    return `${detail}. ${reasonsText}`;
  }

  return detail ?? reasonsText ?? "";
}
